<template>
    <div class="container-fluid" :class="{closed: isClosed}" id="editor" @mousemove="noWriting">
        <nav :class="{cloudy: isWriting}">
            <ol class="breadcrumb">
                <li class="breadcrumb-item"><router-link to="/mypage">マイページ</router-link></li>
                <li class="breadcrumb-item"><router-link :to="booksPath">小説管理</router-link></li>
                <li class="breadcrumb-item"><router-link :to="`/mypage/book/${$route.params.book_id}/sections`">本文一覧</router-link></li>
                <li class="breadcrumb-item">本文編集</li>
            </ol>
        </nav>
        <legend class="text-center my-2">
            <h3 v-if="book.id" class="mincho" v-html="$filters.ruby(book.title)"></h3>
            <h4 class="subtitle mincho" v-if="book.subtitle" v-html="$filters.ruby(book.subtitle)"></h4>
        </legend>
        <div class="container">
            <div class="d-flex justify-content-start">
                <div class="custom-control custom-switch my-2" :class="{cloudy: isWriting}">
                    <input type="checkbox" class="custom-control-input" v-model="isAutosave" id="auto-save-switch">
                    <label class="custom-control-label" for="auto-save-switch">自動保存</label>
                    <div class="text-muted small" v-if="$route.params.id == 'new'">
                        新規記事の場合は、仮保存されます。
                        <span class="text-success">
                            <i class="fas fa-check-circle"></i>
                            保存済み
                        </span>と表示されますが、新規追加しない限りは保存されたことになりません。
                    </div>
                </div>
                <div class="custom-control custom-switch my-2 ml-4" :class="{cloudy: isWriting}" v-if="section.id">
                    <input type="checkbox" class="custom-control-input" v-model="isLive" id="live-switch">
                    <label class="custom-control-label" :class="{'text-danger': isLive}" for="live-switch">
                        ライブ配信
                    </label>
                    <div class="text-muted small">
                        <div v-if="isLive" class="live-url">
                            <a :href="liveUrl" target="_live">{{$filters.truncate(liveUrl, 40)}} <i class="fas fa-external-link-alt"></i></a>
                            <div>
                                このURLをクリックするとライブ画面が開きます。ライブ告知にはライブ画面のURLをお使いください。
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <div class="form-group">
                    <nav class="tools bg-blur d-flex justify-content-between sticky" :class="{cloudy: isWriting}">
                        <ul class="">
                            <li>
                                <a class="nav-link" href="#" @click.prevent="ruby()">
                                    <span class="d-none d-lg-block">
                                        <ruby>ルビ<rt>ルビ</rt></ruby>
                                        (ctrl+1)
                                    </span>
                                    <span class="d-md-block d-lg-none">
                                        <ruby>亜<rt>あ</rt></ruby>
                                    </span>
                                </a>
                            </li>
                            <li>
                                <a class="nav-link" href="#" @click.prevent="emphasis()">
                                    <span class="d-none d-lg-block">
                                        <ruby>傍点<rt>丶丶</rt></ruby>
                                        (ctrl+2)
                                    </span>
                                    <span class="d-md-block d-lg-none">
                                        <ruby>点<rt>丶</rt></ruby>
                                    </span>
                                </a>
                            </li>
                            <li>
                                <a class="nav-link" href="#" @click.prevent="togglePreview()">
                                    <i class="fas fa-eye" v-if="!isPreview"></i>
                                    <i class="fas fa-edit text-primary" v-else></i>
                                </a>
                            </li>
                            <li>
                                <a class="nav-link" href="#" @click.prevent="search()" :class="{disabled: isLive}" id="search">
                                    <i class="fas fa-search"></i>
                                </a>
                            </li>
                            <li>
                                <a class="nav-link" href="#" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    <i class="fas fa-ellipsis-v"></i>
                                </a>
                                <div class="dropdown-menu" aria-labelledby="dropdownMenuLink">
                                    <a class="dropdown-item" href="#" @click.prevent="preview()" :class="{disabled: charAll > 100000}">
                                        <i class="fas fa-file-alt"></i>
                                        実画面プレビュー
                                    </a>
                                    <a class="dropdown-item" href="#" @click.prevent="save()">
                                        <i class="fas fa-save"></i>
                                        保存
                                        <span v-if="isWindows">( Ctrl+S )</span>
                                        <span v-if="isMac">( ⌘+S )</span>
                                    </a>
                                    <a class="dropdown-item" href="#" @click.prevent="indentAll()">
                                        <i class="fas fa-indent"></i>
                                        一字下げ
                                    </a>
                                    <a class="dropdown-item" href="#" @click.prevent="clearRuby()">
                                        <i class="fas fa-eraser"></i>
                                        ルビ全削除
                                    </a>
                                    <a class="dropdown-item" href="#" @click.prevent="showTyping()">
                                        <i class="fas fa-volume-up"></i>
                                        打鍵音...
                                        <span v-if="$store.state.user && $store.state.user.typing_sound == 1">(ｶｶｶｶ...)</span>
                                        <span v-else-if="$store.state.user && $store.state.user.typing_sound == 2">(ﾄﾞﾙﾙﾙ...)</span>
                                        <span v-else-if="$store.state.user && $store.state.user.typing_sound == 3">(ﾄﾄﾄﾄ...)</span>
                                        <span v-else-if="$store.state.user && $store.state.user.typing_sound == 4">(赤)</span>
                                        <span v-else-if="$store.state.user && $store.state.user.typing_sound == 5">(茶)</span>
                                        <span v-else-if="$store.state.user && $store.state.user.typing_sound == 6">(青)</span>
                                    </a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" :class="{disabled: isPreview}" role="button" href="#" @click.prevent="copy('section_name')">
                                        <i class="fas fa-copy"></i>
                                        タイトルコピー
                                    </a>
                                    <a class="dropdown-item" :class="{disabled: isPreview}" role="button" href="#" @click.prevent="copy('body')">
                                        <i class="far fa-copy"></i>
                                        本文コピー
                                    </a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" :class="{disabled: isEdit || !book.is_long}" href="#" @click.prevent="split()">
                                        <i class="fas fa-cut"></i>
                                        カーソル位置で分解...
                                    </a>
                                    <a class="dropdown-item" :class="{disabled: isEdit || !book.is_long}" href="#" @click.prevent="join()">
                                        <i class="fas fa-plus-circle"></i>
                                        次の本文と糾合...
                                    </a>
                                </div>
                            </li>
                        </ul>
                        <div class="m-2 p-2">
                            <div class="d-inline-block">
                                <i class="fas fa-edit text-danger" v-if="isEdit === -1"></i>
                                <i class="fas fa-spinner text-secondary" v-if="isEdit === 1"></i>
                                <i class="fas fa-check-circle text-success" v-if="isEdit === 0"></i>
                                <i class="fas fa-ban text-danger" title="自動保存OFF" v-if="!isAutosave"></i>
                            </div>
                            <div class="d-inline-block" :class="{'text-danger': invalidChars}">
                                <span v-if="selectedChars > 0">{{$filters.numberFormat(selectedChars)}} /</span>
                                {{$filters.numberFormat(charAll)}}文字
                            </div>
                        </div>
                    </nav>
                    <div class="input-group" v-show="!isPreview" v-if="book.is_long">
                        <input type="text" class="form-control section_name"
                            id="section_name"
                            autocomplete="off"
                            @focus="focused = 'section_name'"
                            @keydown.ctrl.1="ruby()"
                            @keydown.ctrl.2="emphasis()"
                            @keydown.ctrl.3="isPreview = !isPreview"
                            @keydown.ctrl.s.prevent="save(true)"
                            @keydown.meta.s.prevent="save(true)"
                            @input="autoSaving()"
                            v-model="section.section_name"
                            :placeholder="`第${section.episode}話`" />
                        <div class="input-group-append" v-if="!section.id">
                            <span class="input-group-text text-danger">未保存</span>
                        </div>
                    </div>
                    <div v-show="isPreview" v-if="book.is_long"
                        class="section_name preview"
                        v-html="$filters.ruby(section.section_name)"></div>
                    <div class="FlexTextarea" v-show="!isPreview">
                        <div class="FlexTextarea__dummy"
                            :class="editorClass"
                            aria-hidden="true">{{section.body + '\u200b'}}</div>
                        <textarea
                            ref="editor"
                            class="FlexTextarea__textarea"
                            :class="editorClass"
                            id="body"
                            @input="autoResize($event)"
                            @focus="focused = 'body'"
                            @keydown.ctrl.1="ruby()"
                            @keydown.ctrl.2="emphasis()"
                            @keydown.ctrl.3="isPreview = !isPreview"
                            @keydown.ctrl.s.prevent="save(true)"
                            @keydown.meta.s.prevent="save(true)"
                            @keydown.exact="writing()"
                            @keydown.shift="writing()"
                            @keyup="countSelection()"
                            @mousedown="countSelection()"
                            @mouseup="countSelection()"
                            @paste="onPaste"
                            @click="noWriting"
                            placeholder="本文を記載してください。"
                            v-model="section.body"></textarea>
                        <div class="overlay" :class="{'d-none': !showOverlay}" @click="textFocus()"></div>
                    </div>
                    <div v-show="isPreview"
                        class="section-body preview"
                        :class="editorClass"
                        id="preview"
                        @click="togglePreview()"
                        v-html="$filters.ruby(section.body)"></div>
                </div>
            </div>
        </div>
        <div class="container text-center my-3">
            <div class="row">
                <div class="col-6" :class="{cloudy: isWriting}">
                    <button type="button" class="btn btn-outline-primary btn-block" @click="save(true)">
                        <i class="fas fa-save"></i>
                        保存
                    </button>
                </div>
                <div class="col-6" :class="{cloudy: isWriting}">
                    <button type="button" class="btn btn-block dropdown-toggle"
                        :class="{
                            'btn-outline-success': isPublished === null,
                            'btn-outline-warning': isPublished === false,
                            'btn-outline-secondary': isPublished,
                        }" @click="showPublish()">
                        <i class="fas fa-upload" v-if="isPublished === null"></i>
                        <i class="far fa-clock" v-else-if="isPublished === false"></i>
                        <i class="fas fa-check-circle" v-else></i>
                        <span v-if="isPublished === null">公開</span>
                        <span v-else-if="isPublished === false">予約中</span>
                        <span v-else>公開中</span>
                    </button>
                </div>
            </div>
        </div>
        <div class="form-group text-center mt-4" :class="{cloudy: isWriting}">
            <router-link :to="`/mypage/book/${this.$route.params.book_id}/sections`">
                戻る
            </router-link>
        </div>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
        <note
            ref="note"
            :book_id="$route.params.book_id"
            :section_id="$route.params.id"
            @toggle="toggle($event, 'note')"
            @searchNote="searchNote"
            :isWriting="isWriting"></note>
        <contents
            ref="contents"
            :disabled="isLive"
            :book_id="$route.params.book_id"
            @toggle="toggle($event, 'contents')"
            v-if="isLong"
            :isWriting="isWriting"></contents>
        <version-list
            ref="version"
            :isEdit="isEdit"
            @toggle="toggle($event, 'version')"
            @selected="selectVersion"
            @edit="editVersion"
            :isWriting="isWriting"></version-list>
        <planner
            ref="planner"
            :book_id="$route.params.book_id"
            @toggle="toggle($event, 'planner')"
            v-if="isLong"
            :isWriting="isWriting"></planner>
        <backgrounds
            ref="backgrounds"
            :section_id="$route.params.id"
            @toggle="toggle($event, 'backgrounds')"
            v-if="isLong"
            :isWriting="isWriting"></backgrounds>
        <search :book_id="$route.params.book_id" :section_id="$route.params.id" :disabled="isEdit" @reload="loadSection();loadContents()"></search>
        <publish-picker :selected="section" :book="book" @save="publish($event)"></publish-picker>
        <split v-bind="episode" @saved="reload()"></split>
        <join :id="section.id" :current="section" @saved="reload()"></join>
        <typing @saved="prepareSound"></typing>
        <version :section_id="section.id" @saved="reloadVersion()" :id="version_id"></version>
        <versions :version_id="version_id" @revert="reloadVersion()"></versions>
        <search-note :book_id="$route.params.book_id" @found="foundNote($event)" @replace="replaceNote($event)"></search-note>
    </div>
</template>

<style scoped lang="scss">
.subtitle {
    font-size: 1.1rem;
    color: #888;
}
#editor {
    padding-right: 640px;
    transition: 0.2s ease;
    &.closed {
        padding-right: 15px;
    }
}
.section_name {
    font-size: 1.4rem;
    padding: 1.5rem 2rem;
    min-height: 84px;
    &:focus {
        -webkit-appearance: none;
        outline: none;
        box-shadow: none !important;
        border-bottom: 1px solid #acb1c4;
    }
}
.section-body {
    padding: 1.5rem 2rem;
    letter-spacing: 0.15rem;
    width: 100%;
    border: 0;
    &.font-s { font-size: 1.0rem; line-height: 2.2rem; }
    &.font-m { font-size: 1.1rem; line-height: 2.4rem; }
    &.font-l { font-size: 1.2rem; line-height: 2.6rem; }
    &.font-x { font-size: 1.3rem; line-height: 2.8rem; }
    &.font-h { font-size: 1.4rem; line-height: 3.0rem; }
    &:focus {
        outline: none;
    }
    &#title {
        font-size: 1.4rem;
    }
}
.caption {
    display: block;
    padding: .5em 0 0;
    font-weight: bold;
    border-bottom: 1px solid #ddd;
    &.no-border {
        border-bottom: 0;
    }
}
.tools {
    ul {
        list-style: none;
        margin: 0px;
        padding: 0em;
        padding-left: 0em;
        vertical-align: middle;
        li {
            display: inline-block;
            margin-left: 0em;
            padding: 0;
            vertical-align: middle;
            .nav-link {
                color: #123;
                padding: 1rem .7rem;
                &:hover {
                    background-color: #e4e4f1;
                }
            }
        }
    }
}
.nav-link.disabled {
    color: #aaa !important;
}

.FlexTextarea {
    position: relative;
    line-height: 2.0rem;
    letter-spacing: 0.15rem;
    .font-s { font-size: 1.0rem; line-height: 2.2rem; }
    .font-m { font-size: 1.1rem; line-height: 2.4rem; }
    .font-l { font-size: 1.2rem; line-height: 2.6rem; }
    .font-x { font-size: 1.3rem; line-height: 2.8rem; }
    .font-h { font-size: 1.4rem; line-height: 3.0rem; }
    .FlexTextarea__dummy {
        overflow: hidden;
        visibility: hidden;
        box-sizing: border-box;
        padding: 1.5rem 2rem;
        min-height: 120px;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
        letter-spacing: inherit;
        border: 0;
    }
    .FlexTextarea__textarea {
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        overflow: hidden;
        box-sizing: border-box;
        padding: 1.5rem 2rem;
        width: 100%;
        height: 100%;
        border: 0;
        border-radius: 4px;
        color: #123;
        letter-spacing: inherit;
        resize: none;
        &:focus {
            outline: none;
        }
    }
}
.live-url {
    font-size: .85rem;
    color: crimson;
}
.container {
    max-width: 960px;
    padding: 0;
}
.overlay {
    display: none;
}
@media screen and (max-width: 1340px) {
    #editor {
        padding-right: 15px;
    }
}
@media screen and (max-width: 770px) {
    .FlexTextarea {
        .font-s { font-size: 0.9rem; line-height: 1.9rem; }
        .font-m { font-size: 1.0rem; line-height: 2.0rem; }
        .font-l { font-size: 1.1rem; line-height: 2.1rem; }
        .font-x { font-size: 1.2rem; line-height: 2.2rem; }
        .font-h { font-size: 1.3rem; line-height: 2.4rem; }
        .FlexTextarea__dummy, .FlexTextarea__textarea {
            padding: 1rem .2rem;
        }
    }
    .section_name {
        font-size: 1.3rem;
    }
    .section-body {
        &.font-s { font-size: 0.9rem; line-height: 1.9rem; }
        &.font-m { font-size: 1.0rem; line-height: 2.0rem; }
        &.font-l { font-size: 1.1rem; line-height: 2.1rem; }
        &.font-x { font-size: 1.2rem; line-height: 2.2rem; }
        &.font-h { font-size: 1.3rem; line-height: 2.4rem; }
    }
    #btn-toggle {
        opacity: .5rem;
    }
    .section-body {
        &.preview {
            padding: 1rem .2rem;
        }
    }
    .overlay {
        display: block;
        position: absolute;
        left: 0; top: 0;
        width: 100%; height: 100%;
        background: rgba(0, 0, 0, .1);
        z-index: 999;
    }
}
</style>

<script>
import note from "./sidebars/note";
import contents from "./sidebars/contents";
import versionList from "./sidebars/versionList";
import planner from "./sidebars/planner";
import backgrounds from "./sidebars/backgrounds";
import search from "./modals/search";
import split from "./modals/split";
import join from "./modals/join";
import publishPicker from "./modals/publishPicker";
import typing from "./modals/typing";
import version from "./modals/version";
import versions from "./modals/versions";
import searchNote from "../notes/searchNote";
const textCaretPos = require('textarea-caret');
export default {
    components: {
        publishPicker,
        search,
        note,
        contents,
        planner,
        backgrounds,
        split,
        join,
        typing,
        version,
        versions,
        versionList,
        searchNote,
    },
    data() {
        return {
            isLoading: false,
            section: {},
            book: {},
            locale: null,
            timeoutID: null,
            liveID: null,
            isAutosave: true,
            isLive: 0,
            isEdit: null,
            isWriting: false,
            isPreview: false,
            isClosed: true,
            instruction: false,
            showOverlay: false,
            org_title: null,
            focused: "body",
            selectedChars: 0,       // 選択文字数
            is_scrolloff: false,    // 最下部貼り付き
            episode: {
                prev: null,
                next: null,
                pos: null,
                section_name: null,
                id: null,
            },
            prepare: null,
            buffer: null,
            audio: null,
            source: null,
            typing: false,
            ruby_body: "",      // 自動ルビ用
            version_id: null,
            mouseMoved: 0,
            routeWatcher: null,
        }
    },
    methods: {
        textFocus() {
            this.showOverlay = false;
            $("#body").focus();
        },
        prepareSound() {
            if (!this.$store.state?.user) {
                // まだユーザー情報が読まれていない
                setTimeout(() => {this.prepareSound()}, 500);
            }
            if (this.$store.state?.user?.typing_sound) {
                axios.get("/wav/se" + this.$store.state.user.typing_sound + ".wav", {responseType: "arraybuffer"}).then(res => {
                    this.prepare = res.data;     // デコード前生データ
                    this.audio = null;
                });
            } else {
                this.audio = null;
                this.source = null;
                this.prepare = null;
            }
        },
        initializeSound() {
            if (!this.prepare) {
                this.prepareSound();
                return;
            }
            window.AudioContext = window.AudioContext || window.webkitAudioContext;
            this.audio = new AudioContext();
            this.audio.decodeAudioData(this.prepare, (buf) => {
                this.buffer = buf;  // デコード済み
                this.source.buffer = buf;
                this.source.loop = false;
            });
            this.source = this.audio.createBufferSource();
            this.source.connect(this.audio.destination);
        },
        writing() {
            if (!this.audio && this.prepare) {
                this.initializeSound();
            }
            if (!this.typing && this.source) {
                this.sound();
            }
            if (!this.isWriting) {
                this.mouseMoved = 0;
                this.isWriting = true;
                this.$emit("writing", true);
            }
        },
        autoResize(ev) {
            if (ev.isComposing) { return; }
            this.autoSaving();
            this.liveCasting();
        },
        liveCasting() {
            if (!this.isLive) return;
            // ライブは１秒単位
            if (this.liveID) {
                clearTimeout(this.liveID);
            }
            this.liveID = setTimeout(() => {this.liveCast()}, 1000);
        },
        liveCast() {
            axios.post("/live/save", this.section);
        },
        autoSaving() {
            this.isEdit = -1;
            if (!this.isAutosave) return;
            // 自動保存は無操作状態で15秒
            if (this.timeoutID) {
                clearTimeout(this.timeoutID);
            }
            let timer = 20000;
            if (timer < this.section?.body?.length) {
                timer = this.section?.body?.length;
            }
            this.timeoutID = setTimeout(() => {this.autoSave()}, timer);
            if (this.focused === "body" && $("#body").get(0).selectionEnd == this.section.body.length) {
                this.scrollBottom();
            }
        },
        autoSave() {
            if (!this.section.body || !this.section.body.trim()) return;
            this.section.book_id = parseInt(this.$route.params.book_id);
            if (this.isEdit > -1) return;
            this.isEdit = 1;
            axios.post("/mypage/book/section/auto-save", this.section).then(res => {
                if (res.data) {
                    this.isEdit = 0;
                }
                if (this.org_title !== this.section.section_name) {
                    this.org_title = this.section.section_name;
                    this.$refs.contents.load();
                }
                if (this.$refs.planner) { this.$refs.planner.load(); }
                if (res.data && res.data.version_updated) {
                    if (this.$refs.version) { this.$refs.version.load(); }
                }
            }).catch(err => {
                console.error(err);
                this.isEdit = -1;
            });
        },
        load() {
            this.loadBook();
            this.loadSection();
        },
        loadSection() {
            this.isLoading = true;
            axios.get(`/mypage/book/section/get`, { params: {
                'id': this.$route.params.id,
                'book_id': this.$route.params.book_id,
            }}).then(res => {
                this.section = res.data;
                if (this.section.is_chapter) {
                    alert.err("章が開かれました。");
                    this.$router.push(`/mypage/book/${this.$route.params.book_id}/sections`);
                    return;
                }
                if (this.section.publish_at) {
                    if (moment(this.section.publish_at).isSameOrBefore(moment())) {
                        this.isAutosave = false;
                    } else {
                        this.isAutosave = true;
                    }
                } else {
                    this.isAutosave = true;
                }
                if (this.$route.query.pos) {
                    try {
                        setTimeout(() => {
                            let start = parseInt(this.$route.query.pos);
                            let end = start + parseInt(this.$route.query.len);
                            $("#body").get(0).setSelectionRange(start, end);
                            setTimeout(() => {
                                const body = document.getElementById("body");
                                const pos = textCaretPos(body, body.selectionEnd)
                                $("body,html").animate({scrollTop: pos.top});
                                document.getElementById("body").focus();
                            });
                        });
                    } catch (error) {
                        console.error(error);
                    }
                }
                this.org_title = this.section.section_name;
                this.section.publish_org = this.section.publish_at;
                setTimeout(() => {this.isEdit = null});
                if (this.$refs["planner"]) { this.$refs.planner.load(); }
            }).catch(err => {
                alert.err(err);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadBook() {
            // 小説読み込み
            axios.get("/mypage/book/get", {params: {id: this.$route.params.book_id}}).then(res => {
                this.book = res.data;
                if (! this.book.is_long) {
                    this.section.is_finish = 1;
                }
            }).catch(err => {
                alert.err(err.response);
            });
        },
        loadContents() {
            // 目次再読み込み
            this.$refs.contents.load();
        },
        save(keep = false) {
            if (! this.section.book_id) {
                this.section.book_id = parseInt(this.$route.params.book_id);
            }
            if (this.isEdit > 0) return;
            this.isLoading = true;
            setTimeout(() => {this.isLoading = false;}, 400);
            this.isEdit = 1;
            axios.post("/mypage/book/section/save", this.section).then(res => {
                alert.msg(res.data.message);
                this.isEdit = 0;
                this.isLoading = false;
                if (!keep) {
                    if (this.isLive) {
                        axios.post("/live/end", {id: this.section.id});
                    }
                    this.$router.push(`/mypage/book/${this.$route.params.book_id}/sections`);
                    return;
                } else if (!this.section.id) {
                    this.$router.push(`/mypage/book/${this.$route.params.book_id}/section/edit/${res.data.id}`);
                    this.load();
                }
                if (this.org_title !== this.section.section_name) {
                    this.org_title = this.section.section_name;
                    this.$refs.contents.load();
                }
                if (this.$refs.planner) { this.$refs.planner.load(); }
                if (res.data && res.data.version_updated) {
                    if (this.$refs.version) { this.$refs.version.load(); }
                }
            }).catch(err => {
                alert.err(err);
                this.section.publish_at = this.section.publish_org;
                this.isEdit = -1;
            });
        },
        ruby() {
            const range = {
                start: $("#" + this.focused).get(0).selectionStart,
                end: $("#" + this.focused).get(0).selectionEnd,
            };
            let word = "";
            if (range.start < range.end) {
                word = this.section[this.focused].slice(range.start, range.end);
            }
            const ruby = "｜" + word + "《ルビを入力…》";
            const before = this.section[this.focused].slice(0, range.start);
            const after = this.section[this.focused].slice(range.end);
            const carret = {
                start: range.start + ruby.length - 7,
                end: range.start + ruby.length - 1
            };
            const regex = /([\|｜][^(《]+|[\u3400-\u9FFF々]+)[\(《]([^\)》]+)[\)》]/mug;
            if (word.match(regex)) {
                // 既存ルビ削除
                word = word.replace(regex, "$1").replace(/[\|｜]/ug, "");
                this.section[this.focused] = before + word + after;
                return;
            }
            this.section[this.focused] = before + ruby + after;
            setTimeout(() => {
                document.getElementById(this.focused).setSelectionRange(carret.start, carret.end);
                document.getElementById(this.focused).focus();
            });
        },
        emphasis() {
            const range = {
                start: $("#" + this.focused).get(0).selectionStart,
                end: $("#" + this.focused).get(0).selectionEnd,
            };
            let word = "";
            if (range.start < range.end) {
                word = this.section[this.focused].slice(range.start, range.end)
            }
            const ruby = "《《"+word+"》》";
            const before = this.section[this.focused].slice(0, range.start);
            const after = this.section[this.focused].slice(range.end);
            const carret = range.start + ruby.length - (word ? 0 : 2);
            if (word.match(/《{2}[^》]+》{2}/gm)) {
                // 既存傍点の削除
                word = word.replace(/《{2}/gm, "").replace(/》{2}/gm, "");
                this.section[this.focused] = before + word + after;
                return;
            }
            this.section[this.focused] = before + ruby + after;
            setTimeout(() => {
                document.getElementById(this.focused).setSelectionRange(carret, carret);
                document.getElementById(this.focused).focus();
            });
        },
        preview() {
            this.section.book = this.book;
            const preview = window.open("//www" + this.$store.state.base_domain + "/mypage/book/preview?book_id=" + this.section.book_id, "preview");
            axios.post("/mypage/book/preview/save", this.section).then(res => {
                preview.location.reload();
            }).catch(err => {
                alert.err(err.response);
            });
        },
        indentAll() {
            let rows = this.section.body.split(/\n/u);
            for ( let i in rows ) {
                if (rows[i].match(/^[^　「（]/u)) {
                    rows[i] = "　" + rows[i];
                }
            }
            this.section.body = rows.join("\n");
        },
        copy(id) {
            if (this.isPreview) return;
            if (id === "body" && this.section.body.match(/《{2}/u)) {
                alert.warn("傍点タグ（《《》》）が含まれます。カクヨム以外に転記される場合は、傍点部分を書き換えてください。");
            }
            if (!navigator.clipboard) {
                $(`#${id}`).select();
                document.execCommand("copy");
                if (id === "section_name") {
                    alert.msg("タイトルをコピーしました。");
                } else {
                    alert.msg("本文をコピーしました。");
                }
            } else {
                let copy = "";
                if (id === "section_name") {
                    copy = this.section.section_name;
                } else {
                    copy = this.section.body;
                }
                navigator.clipboard.writeText(copy).then(() => {
                    if (id === "section_name") {
                        alert.msg("タイトルをコピーしました。");
                    } else {
                        alert.msg("本文をコピーしました。");
                    }
                }).catch(err => { alert.err(err) });
            }
        },
        abortLive(ev) {
            if (this.isLive) {
                let csrf = $("meta[name='csrf-token']").attr("content");
                let payload = new FormData();
                payload.append("_token", csrf);
                payload.append("id", this.section.id);
                navigator.sendBeacon("/live/end", payload);
            }
        },
        split() {
            const range = {
                start: $("#" + this.focused).get(0).selectionStart,
                end: $("#" + this.focused).get(0).selectionEnd,
            };
            this.episode.prev = this.section[this.focused].slice(0, range.start);
            this.episode.next = this.section[this.focused].slice(range.start);
            this.episode.pos = range.start;
            this.episode.section_name = this.section.section_name;
            this.episode.id = this.section.id;
            setTimeout(() => {
                $("#split-modal").modal();
            });
        },
        join() {
            $("#join-modal").modal();
        },
        reload() {
            this.load();
            this.$refs.contents.load();
        },
        toggle(is_closed, ref) {
            this.isClosed = is_closed;
            const refs = [
                "note",
                "contents",
                "planner",
                "version",
                "backgrounds",
            ];
            for (let r of refs) {
                if (r !== ref && this.$refs[r]) {
                    this.$refs[r].toggle(is_closed);
                }
            }
        },
        search() {
            $("#search-modal").modal();
        },
        confirm() {
            return Swal.fire({
                title: "保存されていません",
                html: `<div class="text-left">
                    編集中の本文が保存されていません。
                    このままページを離れますか？
                    </div>`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "crimson",
                cancelButtonColor: "gray",
                confirmButtonText: "離れる",
                cancelButtonText: "キャンセル",
                reverseButtons: true,
            });
        },
        showPublish() {
            $("#publish-modal").modal();
        },
        publish(ev) {
            this.section = ev;
            setTimeout(() => {this.save()});
        },
        clearRuby() {
            this.section.body = this.section.body.replace(/[|｜]/ug, "").replace(/《[^》]*》/ug, "");
            this.autoSaving();
        },
        countSelection() {
            this.selectedChars = $("#body").get(0).selectionEnd - $("#body").get(0).selectionStart;
        },
        scrollBottom() {
            let elm = document.documentElement;
            let clientHeight = elm.clientHeight;
            if (navigator.userAgent.match(/iPhone|Android.+Mobile/)) {
                clientHeight = clientHeight / 2;
            }
            let bottom = elm.scrollHeight - clientHeight;
            window.scroll(0, bottom);
        },
        showTyping() {
            $("#typing-modal").modal();
        },
        sound() {
            this.typing = true;
            this.source.start(0);
            setTimeout(() => {this.typing = false}, 20);
            this.source = this.audio.createBufferSource();
            this.source.buffer = this.buffer;
            this.source.connect(this.audio.destination);
        },
        selectVersion(id) {
            this.version_id = id;
            setTimeout(() => {
                $("#versions-modal").modal();
            });
        },
        reloadVersion() {
            this.load();
            this.$refs.version.load();
        },
        editVersion(id) {
            this.version_id = id;
            setTimeout(() => {
                $("#version-modal").modal();
            });
        },
        noWriting(ev) {
            if (this.isWriting) {
                if (this.mouseMoved++ > 3) {
                    // キャレット＆ポインタ被りで打鍵中にmousemoveが発生するのを無視するため
                    this.isWriting = false;
                    this.$emit("writing", false);
                }
            }
        },
        togglePreview() {
            this.isPreview = !this.isPreview;
        },
        onPaste(ev) {
            const pasted = ev.clipboardData.getData('text');
            const matched = pasted.match(/^https?:\/\/(\d+)\.mitemin\.net\/(i\d+)/);
            if (matched) {
                // 貼り付け停止
                ev.preventDefault();
                // タグ挿入
                const tag = "<" + matched[2] + "|" + matched[1] + ">";
                const range = {
                    start: $("#" + this.focused).get(0).selectionStart,
                    end: $("#" + this.focused).get(0).selectionEnd,
                };
                const before = this.section[this.focused].slice(0, range.start);
                const after = this.section[this.focused].slice(range.end);
                const carret = {
                    start: range.start,
                    end: range.start
                };
                this.section[this.focused] = before + tag + after;
                setTimeout(() => {
                    document.getElementById(this.focused).setSelectionRange(carret, carret);
                    document.getElementById(this.focused).focus();
                });
            }
        },
        searchNote() {
            $("#search-note").modal();
        },
        foundNote(result) {
            this.$refs.note.$refs.notes.found(result);
        },
        replaceNote(words) {
            this.$refs.note.$refs.notes.replaceKeyword(words);
        }
    },
    computed: {
        charAll() {
            if (this.section.body) {
                return this.section.body.length;
            }
            return 0;
        },
        invalidChars() {
            if (this.charAll > 100000) {
                return true;
            }
            return false;
        },
        liveUrl() {
            if (!this.section.id) return null;
            let subdomain = this.$store.state.user.subdomain || "www";
            return document.location.protocol + "//" + subdomain + this.$store.state.base_domain + "/live/" + this.section.id;
        },
        isPublished() {
            if (!this.section.publish_at) {
                return null;
            }
            if (moment(this.section.publish_at).isSameOrBefore(moment())) {
                return true;
            }
            return false;
        },
        isMac() {
            return navigator.userAgent.match(/Macintosh/i);
        },
        isWindows() {
            return navigator.userAgent.match(/Windows/i);
        },
        booksPath() {
            if (this.book.is_hidden === 3) {
                return "/mypage/books/pending";
            } else if (this.book?.property?.is_finished) {
                return "/mypage/books/finished";
            }
            return "/mypage/books/writing";
        },
        isLong() {
            if (this.book.is_long) {
                return true;
            }
            return false;
        },
        editorClass() {
            const cls = {};
            if (this.$store.getters.fontSize) {
                cls[this.$store.getters.fontSize] = true;
            }
            if (this.$store.state.font) {
                cls[this.$store.state.font] = true;
            }
            return cls;
        },
        hasSubdomain() {
            if (this.$store?.state?.user?.subdomain) {
                return true;
            }
            return false;
        }
    },
    watch: {
        isAutosave(n, o) {
            if (n) {
                this.autoSaving();
            }
        },
        isLive(n, o) {
            if (n) {
                axios.post("/live/start", {id: this.section.id});
                this.liveCasting();
                window.open(this.liveUrl, "_live");
            } else {
                axios.post("/live/end", {id: this.section.id});
            }
        },
    },
    mounted() {
        this.prepareSound();
        this.load();
        setTimeout(() => {
            this.useHead({
                title: "マイページ | 貸し本棚",
                link: [ { rel: "canonical", href: location.origin + location.pathname, id: "canonical" } ],
            });
        }, 1000);
    },
    created() {
        window.addEventListener("beforeunload", this.abortLive);
        window.addEventListener("scroll", this.noWriting);
        this.routeWatcher = this.$watch('$route', (n, o) => {
            if (n.params.book_id != this.book.id) {
                this.loadBook();
            }
            this.loadSection();
            if (this.$refs.version) {
                this.$refs.version.load();
            }
        });
    },
    destroyed() {
        window.removeEventListener("beforeunload", this.abortLive);
        window.removeEventListener("scroll", this.noWriting);
    },
    async beforeRouteLeave(to, from) {
        this.$store.dispatch("recent");
        this.routeWatcher();
        if (this.isEdit) {
            let confirm = await this.confirm();
            if (confirm.isConfirmed) {
                axios.post("/live/end", {id: this.section.id});
                return true;
            } else {
                return false;
            }
        } else {
            axios.post("/live/end", {id: this.section.id});
            return true;
        }
    },
    async beforeRouteUpdate(n, o) {
        if (this.isEdit) {
            let confirm = await this.confirm();
            if (confirm.isConfirmed) {
                axios.post("/live/end", {id: this.section.id});
                return true;
            } else {
                return false;
            }
        } else {
            axios.post("/live/end", {id: this.section.id});
            return true;
        }
    }
}
</script>